import { html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { LandiStorefrontElement } from '../base/landi-storefront-element.js';
import { sharedStyles, inputStyles } from '../styles/shared.js';
import {
  resolveProductImageUrl,
  getProductImageDisplay,
  type Product,
  formatPrice,
} from '@landi/store-core';
import {
  getCachedProducts,
  prefetchProductCatalog,
  searchCachedProducts,
} from '../utils/product-catalog-cache.js';

const SEARCH_DEBOUNCE_MS = 200;

/**
 * Product search input with autocomplete dropdown
 *
 * @element landi-product-search
 * @fires search-submit - Fired when search is submitted
 * @fires product-select - Fired when a product is selected from dropdown
 */
@customElement('landi-product-search')
export class LandiProductSearch extends LandiStorefrontElement {
  static styles = [
    sharedStyles,
    inputStyles,
    css`
      :host {
        display: block;
        position: relative;
        z-index: 1;
      }

      :host(:focus-within) {
        z-index: 200;
      }

      .search-wrapper {
        position: relative;
      }

      .search-input-container {
        position: relative;
        display: flex;
        align-items: center;
      }

      .search-icon {
        position: absolute;
        left: 1rem;
        width: 20px;
        height: 20px;
        color: var(--landi-text-light);
        pointer-events: none;
      }

      .search-input {
        width: 100%;
        padding: 0.875rem 1rem 0.875rem 3rem;
        font-size: 0.875rem;
        font-family: var(--landi-font-sans);
        background: var(--landi-surface);
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius-md);
        color: var(--landi-text);
        transition: border-color 200ms ease, box-shadow 200ms ease;
      }

      .search-input:focus {
        outline: none;
        border-color: var(--landi-primary);
        box-shadow: 0 0 0 3px var(--landi-primary-light);
      }

      .search-input::placeholder {
        color: var(--landi-text-light);
      }

      .clear-btn {
        position: absolute;
        right: 1rem;
        padding: 0.25rem;
        background: transparent;
        border: none;
        color: var(--landi-text-light);
        cursor: pointer;
        transition: color 200ms ease;
      }

      .clear-btn:hover {
        color: var(--landi-text);
      }

      .dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        margin-top: 0.5rem;
        background: var(--landi-surface);
        border: 1px solid var(--landi-border);
        border-radius: var(--landi-radius-md);
        box-shadow: var(--landi-shadow-lg);
        z-index: 200;
        max-height: 400px;
        overflow-y: auto;
      }

      .dropdown-item {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 0.75rem 1rem;
        cursor: pointer;
        transition: background 200ms ease;
        border-bottom: 1px solid var(--landi-border-light);
      }

      .dropdown-item:last-child {
        border-bottom: none;
      }

      .dropdown-item:hover {
        background: var(--landi-background);
      }

      .dropdown-item:focus {
        outline: none;
        background: var(--landi-background);
      }

      .item-image {
        width: 48px;
        height: 48px;
        object-fit: cover;
        border-radius: var(--landi-radius);
        background: var(--landi-background);
        flex-shrink: 0;
      }

      .item-details {
        flex: 1;
        min-width: 0;
      }

      .item-name {
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--landi-text);
        margin: 0 0 0.25rem;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .item-category {
        font-size: 0.75rem;
        color: var(--landi-text-muted);
        margin: 0;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      .item-price {
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--landi-text);
        flex-shrink: 0;
      }

      .no-results,
      .search-status {
        padding: 1.25rem 1rem;
        text-align: center;
        color: var(--landi-text-muted);
        font-size: 0.875rem;
      }

      .search-status {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.625rem;
      }

      .spinner {
        width: 18px;
        height: 18px;
        border: 2px solid var(--landi-border);
        border-top-color: var(--landi-primary);
        border-radius: 50%;
        animation: landi-search-spin 0.7s linear infinite;
        flex-shrink: 0;
      }

      @keyframes landi-search-spin {
        to {
          transform: rotate(360deg);
        }
      }

      .search-hint {
        padding: 1rem;
        text-align: center;
        font-size: 0.813rem;
        color: var(--landi-text-light);
      }

      .view-all-link {
        display: block;
        padding: 0.75rem 1rem;
        text-align: center;
        font-size: 0.813rem;
        font-weight: 600;
        color: var(--landi-primary);
        background: var(--landi-background);
        border-top: 1px solid var(--landi-border);
        cursor: pointer;
        transition: color 200ms ease;
        text-decoration: none;
      }

      .view-all-link:hover {
        color: var(--landi-primary-hover);
      }

      /* Highlight matching text */
      .highlight {
        background: var(--landi-primary-light);
        color: var(--landi-primary);
        padding: 0 2px;
        border-radius: 2px;
      }
    `,
  ];

  @property({ type: String }) placeholder = 'Search products...';
  @property({ type: Number, attribute: 'min-chars' }) minChars = 2;
  @property({ type: Number, attribute: 'max-results' }) maxResults = 5;
  @property({ type: String, attribute: 'results-href' }) resultsHref = 'products.html';
  @property({ type: String, attribute: 'result-href-base' }) resultHrefBase = 'product.html?id=';

  @state() private query = '';
  @state() private results: Product[] = [];
  @state() private isOpen = false;
  @state() private isSearching = false;
  @state() private activeIndex = -1;
  @state() private searchError = '';

  private debounceTimer?: number;
  private searchRequestId = 0;
  private lastSearchQuery = '';

  connectedCallback(): void {
    super.connectedCallback();
    void prefetchProductCatalog().catch(() => {
      /* Prefetch failure is handled when the user searches. */
    });
  }

  private handleInput(e: Event) {
    const input = e.target as HTMLInputElement;
    this.query = input.value;
    this.activeIndex = -1;
    this.searchError = '';

    clearTimeout(this.debounceTimer);

    if (this.query.length >= this.minChars) {
      this.isOpen = true;
      if (this.query !== this.lastSearchQuery) {
        this.isSearching = true;
      }
    } else {
      this.results = [];
      this.isOpen = false;
      this.isSearching = false;
      this.lastSearchQuery = '';
      return;
    }

    this.debounceTimer = window.setTimeout(() => {
      void this.performSearch();
    }, SEARCH_DEBOUNCE_MS);
  }

  private applySearchResults(query: string, results: Product[]): void {
    this.lastSearchQuery = query;
    this.results = results.slice(0, this.maxResults);
    this.isOpen = true;
  }

  private async performSearch() {
    const query = this.query;

    if (query.length < this.minChars) {
      this.results = [];
      this.isOpen = false;
      this.isSearching = false;
      this.searchError = '';
      this.lastSearchQuery = '';
      return;
    }

    if (query === this.lastSearchQuery && getCachedProducts()) {
      this.isSearching = false;
      return;
    }

    const requestId = ++this.searchRequestId;
    this.isSearching = true;
    this.isOpen = true;
    this.searchError = '';

    try {
      const results = await searchCachedProducts(query);
      if (requestId !== this.searchRequestId || query !== this.query) {
        return;
      }
      this.applySearchResults(query, results);
    } catch (err) {
      if (requestId !== this.searchRequestId || query !== this.query) {
        return;
      }
      this.results = [];
      this.isOpen = true;
      this.searchError = err instanceof Error ? err.message : 'Search failed';
    } finally {
      if (requestId === this.searchRequestId && query === this.query) {
        this.isSearching = false;
      }
    }
  }

  private handleFocus() {
    if (this.query.length >= this.minChars) {
      this.isOpen = true;
      if (this.query !== this.lastSearchQuery) {
        void this.performSearch();
      }
    }
  }

  private handleBlur() {
    // Delay to allow click on dropdown items
    setTimeout(() => {
      this.isOpen = false;
    }, 200);
  }

  private handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (!this.isSearching && this.isOpen && this.activeIndex >= 0 && this.results[this.activeIndex]) {
        this.selectProduct(this.results[this.activeIndex]);
      } else if (this.query.trim().length >= this.minChars) {
        this.submitSearch();
      }
      return;
    }

    if (e.key === 'Escape') {
      this.isOpen = false;
      return;
    }

    if (!this.isOpen || this.isSearching) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.activeIndex = Math.min(this.activeIndex + 1, this.results.length - 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, -1);
        break;
    }
  }

  private selectProduct(product: Product) {
    this.dispatchEvent(
      new CustomEvent('product-select', {
        bubbles: true,
        composed: true,
        detail: { product },
      })
    );

    window.location.href = `${this.resultHrefBase}${product.id}`;
  }

  private buildResultsUrl(query: string): string {
    const encoded = encodeURIComponent(query);
    const hashIndex = this.resultsHref.indexOf('#');

    if (hashIndex >= 0) {
      const path = this.resultsHref.slice(0, hashIndex);
      const hash = this.resultsHref.slice(hashIndex);
      const separator = path.includes('?') ? '&' : '?';
      return `${path}${separator}q=${encoded}${hash}`;
    }

    const separator = this.resultsHref.includes('?') ? '&' : '?';
    return `${this.resultsHref}${separator}q=${encoded}`;
  }

  private submitSearch() {
    this.dispatchEvent(
      new CustomEvent('search-submit', {
        bubbles: true,
        composed: true,
        detail: { query: this.query },
      })
    );

    window.location.href = this.buildResultsUrl(this.query);
  }

  private clearSearch() {
    this.query = '';
    this.results = [];
    this.isOpen = false;
    this.isSearching = false;
    this.searchError = '';
    this.lastSearchQuery = '';
    this.searchRequestId += 1;
    clearTimeout(this.debounceTimer);
    this.shadowRoot?.querySelector<HTMLInputElement>('.search-input')?.focus();
  }

  private escapeRegExp(text: string): string {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  private highlightMatch(text: string, query: string): string {
    if (!query) return text;
    const regex = new RegExp(`(${this.escapeRegExp(query)})`, 'gi');
    return text.replace(regex, '<span class="highlight">$1</span>');
  }

  private renderDropdownContent() {
    if (this.isSearching) {
      return html`
        <div class="search-status" role="status" aria-live="polite">
          <span class="spinner" aria-hidden="true"></span>
          <span>Searching…</span>
        </div>
      `;
    }

    if (this.results.length > 0) {
      return html`
        ${this.results.map((product, index) => {
          const imageDisplay = getProductImageDisplay({
            url: resolveProductImageUrl(product.images[0]?.url),
            name: product.name,
            sku: product.sku,
            category: product.category,
            theme: 'light',
          });
          return html`
            <div
              class="dropdown-item"
              role="option"
              tabindex="0"
              aria-selected=${index === this.activeIndex}
              @click=${() => this.selectProduct(product)}
              @mouseenter=${() => (this.activeIndex = index)}
            >
              <img
                class="item-image"
                src=${imageDisplay.src}
                alt=""
                .onerror=${imageDisplay.onError}
              />
              <div class="item-details">
                <p class="item-name" .innerHTML=${this.highlightMatch(product.name, this.query)}></p>
                <p class="item-category">${product.category || 'Product'}</p>
              </div>
              <span class="item-price">${formatPrice(product.price)}</span>
            </div>
          `;
        })}

        <a href="${this.buildResultsUrl(this.query)}" class="view-all-link">
          View all results for "${this.query}"
        </a>
      `;
    }

    if (this.searchError) {
      return html`
        <div class="no-results" role="alert">
          <p>${this.searchError}</p>
        </div>
      `;
    }

    if (this.query.length >= this.minChars) {
      return html`
        <div class="no-results">
          <p>No products found for "${this.query}"</p>
        </div>
      `;
    }

    return html`
      <div class="search-hint">
        <p>Type at least ${this.minChars} characters to search</p>
      </div>
    `;
  }

  render() {
    return html`
      <div class="search-wrapper">
        <div class="search-input-container">
          <svg
            class="search-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <path d="m21 21-4.35-4.35"></path>
          </svg>

          <input
            type="text"
            class="search-input"
            .value=${this.query}
            placeholder=${this.placeholder}
            @input=${this.handleInput}
            @focus=${this.handleFocus}
            @blur=${this.handleBlur}
            @keydown=${this.handleKeydown}
            aria-label="Search products"
            aria-autocomplete="list"
            aria-expanded=${this.isOpen}
            aria-busy=${this.isSearching}
          />

          ${this.query
            ? html`
                <button class="clear-btn" @click=${this.clearSearch} aria-label="Clear search">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              `
            : ''}
        </div>

        ${this.isOpen
          ? html` <div class="dropdown" role="listbox">${this.renderDropdownContent()}</div> `
          : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'landi-product-search': LandiProductSearch;
  }
}
